import { useEffect, useRef, useState, useMemo } from "react";
import { AlertCircle, ImagePlus, PlusCircle, Trash2, Pencil, X } from "lucide-react";
import { api, type ApiCategory, type ApiVideo } from "@/lib/api";
import { SortableList } from "@/components/SortableList";

export default function AdminVideosTab() {
  const [videos, setVideos] = useState<ApiVideo[]>([]);
  const [cats, setCats] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: "",
    vod_url: "",
    category_id: 0,
    description: "",
  });
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ApiVideo | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function load() {
    setLoading(true);
    Promise.all([api.listVideos(), api.listCategories()])
      .then(([v, c]) => {
        setVideos(v.videos);
        setCats(c.categories);
        if (c.categories.length > 0 && form.category_id === 0) {
          setForm((f) => ({ ...f, category_id: c.categories[0].id }));
        }
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function pickCover(f: File | null) {
    setCoverFile(f);
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview(f ? URL.createObjectURL(f) : null);
  }

  async function add() {
    setErr(null);
    if (!form.title.trim()) { setErr("视频名称必填"); return; }
    if (!form.vod_url.trim()) { setErr("VOD 链接必填"); return; }
    if (!form.category_id) { setErr("请选择分类"); return; }
    if (!coverFile) { setErr("请选择封面图"); return; }

    const fd = new FormData();
    fd.set("title", form.title.trim());
    fd.set("vod_url", form.vod_url.trim());
    fd.set("category_id", String(form.category_id));
    if (form.description.trim()) fd.set("description", form.description.trim());
    fd.set("cover", coverFile);

    setSaving(true);
    try {
      await api.createVideo(fd);
      setForm({ title: "", vod_url: "", category_id: cats[0]?.id || 0, description: "" });
      pickCover(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  }

  async function del(v: ApiVideo) {
    if (!confirm(`确认删除视频 "${v.title}"？此操作不可撤销。`)) return;
    try {
      await api.deleteVideo(v.id);
      load();
    } catch (e) { alert((e as Error).message); }
  }

  if (cats.length === 0 && !loading) {
    return (
      <div className="rounded-xl border border-dashed border-surface-border bg-white p-10 text-center">
        <p className="text-sm text-ink-400">还没有分类。请先去「分类」标签页创建至少一个分类，再来添加视频。</p>
      </div>
    );
  }

  return (
    <>
      <div className="card mb-6 p-5">
        <div className="mb-4 text-sm font-semibold text-ink-900">添加视频</div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px]">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">视频名称</span>
              <input className="input" placeholder="例如：OET Speaking Role-play 1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">腾讯 VOD 链接</span>
              <input className="input font-mono text-xs" placeholder="https://...mp4 或 https://...m3u8 或 https://...iframe" value={form.vod_url} onChange={(e) => setForm({ ...form, vod_url: e.target.value })} />
              <p className="mt-1 text-[11px] text-ink-400">支持 mp4 / m3u8 直链，或腾讯 VOD 提供的 iframe 嵌入链接。</p>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">分类</span>
                <select className="input" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: Number(e.target.value) })}>
                  <option value={0} disabled>请选择…</option>
                  {cats.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
              <div />
            </div>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">简介（可选）</span>
              <textarea className="input min-h-[60px]" rows={2} placeholder="一两句话介绍这个视频的内容…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
          </div>

          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">封面图</span>
            <label className="relative flex aspect-video w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border border-dashed border-surface-border bg-surface-muted hover:bg-surface-muted/60">
              {coverPreview ? (
                <img src={coverPreview} alt="" className="h-full w-full object-cover" />
              ) : (
                <>
                  <ImagePlus className="h-6 w-6 text-ink-400" />
                  <span className="mt-2 text-[11px] text-ink-400">点击选择图片</span>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="absolute inset-0 cursor-pointer opacity-0"
                onChange={(e) => pickCover(e.target.files?.[0] || null)}
              />
            </label>
            <p className="mt-1 text-[11px] text-ink-400">jpg/png/webp，建议 16:9，&lt;10MB</p>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <button className="btn-primary" onClick={add} disabled={saving}>
            <PlusCircle className="h-4 w-4" /> {saving ? "添加中…" : "添加视频"}
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-surface-border bg-white">
        <div className="border-b border-surface-border bg-surface-muted px-5 py-3 text-[11px] uppercase tracking-wider text-ink-400">
          视频库 · 按分类分组 · 拖动 <span className="font-mono">⋮⋮</span> 调整顺序（前台视频库会同步显示）
        </div>
        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-ink-400">加载中…</div>
        ) : videos.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-ink-400">还没有视频</div>
        ) : (
          <VideosGrouped
            videos={videos}
            cats={cats}
            onEdit={(v) => setEditing(v)}
            onDelete={del}
            onReload={load}
          />
        )}
      </div>

      {editing && (
        <EditVideoModal
          video={editing}
          categories={cats}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

interface EditModalProps {
  video: ApiVideo;
  categories: ApiCategory[];
  onClose: () => void;
  onSaved: () => void;
}

function EditVideoModal({ video, categories, onClose, onSaved }: EditModalProps) {
  const [title, setTitle] = useState(video.title);
  const [vodUrl, setVodUrl] = useState(video.vod_url);
  const [categoryId, setCategoryId] = useState(video.category_id);
  const [description, setDescription] = useState(video.description || "");
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function pickCover(f: File | null) {
    setCoverFile(f);
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview(f ? URL.createObjectURL(f) : null);
  }

  async function save() {
    setErr(null);
    if (!title.trim()) { setErr("视频名称必填"); return; }
    if (!vodUrl.trim()) { setErr("VOD 链接必填"); return; }
    if (!categoryId) { setErr("请选择分类"); return; }

    const fd = new FormData();
    fd.set("title", title.trim());
    fd.set("vod_url", vodUrl.trim());
    fd.set("category_id", String(categoryId));
    fd.set("description", description.trim());
    if (coverFile) fd.set("cover", coverFile);

    setSaving(true);
    try {
      await api.updateVideo(video.id, fd);
      onSaved();
    } catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-10 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-surface-border px-6 py-4">
          <div>
            <h2 className="font-display text-lg font-semibold text-ink-900">编辑视频</h2>
            <p className="mt-0.5 text-[11px] text-ink-400">ID #{video.id}</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-ink-400 hover:bg-surface-muted hover:text-ink-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 px-6 py-5 lg:grid-cols-[1fr_220px]">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">视频名称</span>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">腾讯 VOD 链接</span>
              <input className="input font-mono text-xs" value={vodUrl} onChange={(e) => setVodUrl(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">分类</span>
              <select className="input" value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value))}>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">简介</span>
              <textarea
                className="input min-h-[120px]"
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-ink-400">{description.length} 字</p>
            </label>
          </div>

          <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">封面图</span>
            <label className="relative flex aspect-video w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border border-dashed border-surface-border bg-surface-muted hover:bg-surface-muted/60">
              <img
                src={coverPreview || video.cover_path}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 opacity-0 transition hover:opacity-100">
                <ImagePlus className="h-6 w-6 text-white" />
                <span className="mt-1 text-[11px] font-semibold text-white">点击替换</span>
              </div>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="absolute inset-0 cursor-pointer opacity-0"
                onChange={(e) => pickCover(e.target.files?.[0] || null)}
              />
            </label>
            <p className="mt-1 text-[11px] text-ink-400">
              {coverFile ? `新封面：${(coverFile.size / 1024).toFixed(0)} KB` : "保持原封面（点图替换）"}
            </p>
          </div>
        </div>

        {err && (
          <div className="mx-6 mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4" /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-surface-border px-6 py-4">
          <button onClick={onClose} className="btn-ghost" disabled={saving}>取消</button>
          <button onClick={save} className="btn-primary" disabled={saving}>
            {saving ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface VideosGroupedProps {
  videos: ApiVideo[];
  cats: ApiCategory[];
  onEdit: (v: ApiVideo) => void;
  onDelete: (v: ApiVideo) => void;
  onReload: () => void;
}

function VideosGrouped({ videos, cats, onEdit, onDelete, onReload }: VideosGroupedProps) {
  const grouped = useMemo(() => {
    const map = new Map<number, ApiVideo[]>();
    videos.forEach((v) => {
      const arr = map.get(v.category_id) || [];
      arr.push(v);
      map.set(v.category_id, arr);
    });
    return cats
      .map((c) => ({ cat: c, vids: map.get(c.id) || [] }))
      .filter((g) => g.vids.length > 0);
  }, [videos, cats]);

  return (
    <div>
      {grouped.map(({ cat, vids }) => (
        <div key={cat.id} className="border-b border-surface-border last:border-b-0">
          <div className="flex items-center justify-between bg-surface-muted/40 px-5 py-2.5">
            <div className="flex items-baseline gap-3">
              <div className="text-sm font-semibold text-ink-900">{cat.name}</div>
              <div className="text-[11px] text-ink-400">{vids.length} 个视频</div>
            </div>
          </div>
          <SortableList
            items={vids}
            onReorder={async (orderedIds) => {
              await api.reorderVideos(cat.id, orderedIds);
              onReload();
            }}
            renderItem={(v) => (
              <div className="flex items-center gap-3 py-2 pr-3">
                <img src={v.cover_path} alt="" className="h-12 w-20 flex-shrink-0 rounded-sm object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink-900">{v.title}</div>
                  <div className="truncate font-mono text-[10px] text-ink-400">{v.vod_url}</div>
                </div>
                <div className="hidden text-xs text-ink-400 lg:block">{v.created_at?.slice(0, 10)}</div>
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <button
                    onClick={() => onEdit(v)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-600 hover:bg-surface-muted"
                  >
                    <Pencil className="h-3 w-3" /> 编辑
                  </button>
                  <button
                    onClick={() => onDelete(v)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" /> 删除
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      ))}
    </div>
  );
}
